import React, { useState } from 'react';

function Resources() {
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedResource, setSelectedResource] = useState(null);

  const tags = [
    { id: 'all', label: 'All' },
    { id: 'videos', label: 'Videos', icon: '🎥' },
    { id: 'webinars', label: 'Webinars', icon: '💻' },
    { id: 'blogs', label: 'Blogs', icon: '📝' },
    { id: 'ebooks', label: 'eBooks', icon: '📚' },
    { id: 'reports', label: 'Reports', icon: '📊' },
    { id: 'brochures', label: 'Brochures', icon: '📑' },
    { id: 'news', label: 'News', icon: '📰' },
    { id: 'articles', label: 'Articles', icon: '📄' },
    { id: 'case-studies', label: 'Case Studies', icon: '🔍' }
  ];

  const resources = [
    {
      id: 1,
      type: 'videos',
      title: 'Maximize Your Practice Efficiency',
      author: 'Dr. Gregory M.',
      date: 'September 22, 2023',
      description: 'Dr. Greg Wu of Eversmile Dental shares his experience maximizing...',
      thumbnail: '/assets/video-thumbnail-1.webp',
      tags: ['Practice Management', 'Efficiency'],
      fullContent: {
        overview: 'Learn how Dr. Greg Wu transformed his dental practice using modern practice management solutions.',
        keyPoints: [
          'Reduced administrative time by 45%',
          'Increased patient satisfaction to 98%',
          'Streamlined scheduling and billing processes',
          'Improved team collaboration and communication'
        ],
        features: [
          {
            title: 'Smart Scheduling',
            description: 'AI-powered scheduling system that optimizes appointment slots and reduces no-shows'
          },
          {
            title: 'Financial Analytics',
            description: 'Real-time financial reporting and analytics to track practice performance'
          },
          {
            title: 'Team Management',
            description: 'Comprehensive staff scheduling and performance tracking tools'
          },
          {
            title: 'Patient Communication',
            description: 'Automated appointment reminders and patient engagement features'
          }
        ],
        testimonial: {
          quote: "The practice management system has revolutionized how we operate. We're more efficient than ever, and our patients love the modern experience.",
          author: "Dr. Greg Wu",
          position: "Owner, Eversmile Dental"
        }
      }
    },
    {
      id: 2,
      type: 'webinars',
      title: 'Streamline Your Practice Workflow',
      author: 'Dr. Sarah Thompson',
      date: 'September 25, 2023',
      description: "Adapting Curve Dental's cloud-based system streamlined her...",
      thumbnail: '/assets/webinar-thumbnail-1.webp',
      tags: ['Workflow', 'Cloud Solutions']
    },
    {
      id: 3,
      type: 'case-studies',
      title: 'Transforming Patient Care',
      author: 'Dr. Chad DuPont',
      date: 'October 11, 2023',
      description: 'Dr. Chad DuPont discusses why cloud-based software is essential...',
      thumbnail: '/assets/case-study-1.webp',
      tags: ['Patient Care', 'Cloud Technology']
    },
    // Add more resources as needed
  ];

  const filteredResources = selectedTag === 'all' 
    ? resources 
    : resources.filter(resource => resource.type === selectedTag);

  // Modal component
  const ResourceModal = ({ resource, onClose }) => {
    if (!resource) return null;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#1E2A4A]">{resource.title}</h2>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="p-6">
            <img 
              src={resource.thumbnail} 
              alt={resource.title}
              className="w-full h-64 object-cover rounded-xl mb-6"
            />
            
            <div className="space-y-8">
              {/* Overview */}
              <div>
                <h3 className="text-xl font-semibold text-[#1E2A4A] mb-3">Overview</h3>
                <p className="text-gray-600">{resource.fullContent.overview}</p>
              </div>

              {/* Key Points */}
              <div>
                <h3 className="text-xl font-semibold text-[#1E2A4A] mb-3">Key Achievements</h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {resource.fullContent.keyPoints.map((point, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-2 text-orange-500">✓</span>
                      <span className="text-gray-600">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Features */}
              <div>
                <h3 className="text-xl font-semibold text-[#1E2A4A] mb-3">Key Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {resource.fullContent.features.map((feature, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-xl">
                      <h4 className="font-semibold text-[#1E2A4A] mb-2">{feature.title}</h4>
                      <p className="text-gray-600 text-sm">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Testimonial */}
              <div className="bg-[#E5F0F3] p-6 rounded-xl">
                <blockquote className="text-lg text-gray-700 italic mb-4">
                  "{resource.fullContent.testimonial.quote}"
                </blockquote>
                <div className="flex items-center">
                  <div>
                    <div className="font-semibold text-[#1E2A4A]">
                      {resource.fullContent.testimonial.author}
                    </div>
                    <div className="text-sm text-gray-500">
                      {resource.fullContent.testimonial.position}
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center">
                <button className="bg-orange-500 text-white px-8 py-3 rounded-full font-medium hover:bg-orange-600 transition-colors">
                  Schedule a Demo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pt-24 bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <div className="bg-[#E5F0F3] rounded-b-[100px] mb-16">
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#1E2A4A] mb-6">
              Welcome to the Resource Center
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Explore our useful case studies and testimonials from more than 20,000 dental practice owners and professionals using Curve.
            </p>
            <div className="relative max-w-2xl mx-auto">
              <input
                type="search"
                placeholder="Search resources..."
                className="w-full px-6 py-4 rounded-full border border-gray-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <button className="absolute right-4 top-1/2 transform -translate-y-1/2">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tags */}
      <div className="container mx-auto px-6 mb-12">
        <div className="flex flex-wrap gap-4 justify-center">
          {tags.map(tag => (
            <button
              key={tag.id}
              onClick={() => setSelectedTag(tag.id)}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                selectedTag === tag.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              } shadow-sm`}
            >
              <span className="mr-2">{tag.icon}</span>
              {tag.label}
            </button>
          ))}
        </div>
      </div>

      {/* Resources Grid */}
      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredResources.map(resource => (
            <div key={resource.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="relative">
                <img 
                  src={resource.thumbnail} 
                  alt={resource.title}
                  className="w-full h-48 object-cover rounded-t-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-t-xl">
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex gap-2 mb-2">
                      {resource.tags.map((tag, index) => (
                        <span key={index} className="px-3 py-1 bg-white/20 rounded-full text-white text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="text-sm text-gray-500 mb-2">
                  Published: {resource.date}
                </div>
                <h3 className="text-xl font-bold text-[#1E2A4A] mb-2">
                  {resource.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {resource.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    By {resource.author}
                  </span>
                  <button 
                    onClick={() => setSelectedResource(resource)}
                    className="text-orange-500 font-medium hover:text-orange-600 transition-colors"
                  >
                    Learn More →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="bg-[#1E2A4A] text-white mt-16">
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">
              Sign up for our newsletter
            </h2>
            <p className="text-gray-300 mb-8">
              Stay updated with the latest insights, tips, and news about dental practice management.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-xl mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-grow px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <button className="px-8 py-3 bg-orange-500 text-white font-medium rounded-full hover:bg-orange-600 transition-colors">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <ResourceModal 
        resource={selectedResource} 
        onClose={() => setSelectedResource(null)} 
      />
    </div>
  );
}

export default Resources; 